import { create } from 'zustand';
import { ChartEvent, StateType } from '../chart/types';

interface CareFlow360State {
  events: ChartEvent[];
  pendingSaves: ChartEvent[];
  pendingDeletes: string[];
  visitVersion: number;
  selectedTeeth: number[];
  mode: StateType;
  notation: 'iso' | 'palmer' | 'universal';
  showPrimary: boolean;

  setMode: (_mode: StateType) => void;
  setNotation: (_notation: 'iso' | 'palmer' | 'universal') => void;
  togglePrimary: () => void;
  showPermanent: boolean;
  togglePermanent: () => void;
  
  toggleToothSelection: (_toothId: number) => void;
  clearSelection: () => void;
  
  addEvent: (_event: ChartEvent) => void;
  updateEvent: (_id: string, _patch: Partial<ChartEvent>) => void;
  removeEvent: (_id: string) => void;
  undoLastEvent: () => void;
  clearPendingSaves: () => void;
  setEvents: (_events: ChartEvent[]) => void;
  setVisitVersion: (_visitVersion: number) => void;
}

export const useCareFlow360Store = create<CareFlow360State>((set) => ({
  events: [],
  pendingSaves: [],
  pendingDeletes: [],
  visitVersion: 0,
  selectedTeeth: [],
  mode: 'state',
  notation: 'iso',
  showPrimary: false,
  showPermanent: true,

  setMode: (mode) => set({ mode }),
  setNotation: (notation) => set({ notation }),
  togglePrimary: () => set((state) => ({ showPrimary: !state.showPrimary })),
  togglePermanent: () => set((state) => ({ showPermanent: !state.showPermanent })),

  toggleToothSelection: (toothId) => set((state) => ({
    selectedTeeth: state.selectedTeeth.includes(toothId)
      ? state.selectedTeeth.filter((id) => id !== toothId)
      : [...state.selectedTeeth, toothId]
  })),

  clearSelection: () => set({ selectedTeeth: [] }),

  addEvent: (event) => set((state) => ({
    events: [...state.events, event],
    pendingSaves: [...state.pendingSaves, event],
    selectedTeeth: [], 
  })),

  updateEvent: (id, patch) => set((state) => ({
    events: state.events.map((event) =>
      event.id === id ? { ...event, ...patch } : event
    ),
    pendingSaves: state.pendingSaves.map((event) =>
      event.id === id ? { ...event, ...patch } : event
    ),
  })),

  removeEvent: (id) => set((state) => {
    const inPending = state.pendingSaves.some(e => e.id === id);
    return {
      events: state.events.filter((e) => e.id !== id),
      pendingSaves: state.pendingSaves.filter((e) => e.id !== id),
      pendingDeletes: inPending ? state.pendingDeletes : [...state.pendingDeletes, id],
    };
  }),

  undoLastEvent: () => set((state) => {
    const last = state.events[state.events.length - 1];
    if (!last) return state;
    const inPending = state.pendingSaves.some(e => e.id === last.id);
    return {
      events: state.events.slice(0, -1),
      pendingSaves: state.pendingSaves.filter((e) => e.id !== last.id),
      pendingDeletes: inPending ? state.pendingDeletes : [...state.pendingDeletes, last.id],
    };
  }),

  clearPendingSaves: () => set({ pendingSaves: [], pendingDeletes: [] }),

  setEvents: (events) => set({ events, selectedTeeth: [], pendingSaves: [], pendingDeletes: [] }),
  setVisitVersion: (visitVersion) =>
    set({ visitVersion: Number.isFinite(visitVersion) && visitVersion >= 0 ? visitVersion : 0 }),
}));
